/** @format */

import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { usePasswordReset } from '../hooks/usePasswordReset';
import { PasswordRequirements } from './PasswordRequirements';
import Key from 'img/Key.svg';
import Envelope from 'img/EnvelopeSimple.svg';
import ArrowLeft from 'img/ArrowLineLeft.svg';

/**
 * PasswordResetForm Component
 * Multi-step form for resetting user password
 */
export const PasswordResetForm: React.FC = () => {
  const {
    step,
    loading,
    error,
    formData,
    setStep,
    updateFormData,
    initiateReset,
    confirmReset,
    clearError,
  } = usePasswordReset();

  // Show error toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    updateFormData(name as keyof typeof formData, value);
  };

  const handleInitiateReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await initiateReset();
    if (success) {
      toast.success('Reset code sent successfully');
    }
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.confirmationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }
    setStep(3);
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await confirmReset();
    if (success) {
      toast.success('Password reset successfully');
    }
  };

  const handleGoToSignIn = () => {
    window.location.href = '/signin';
  };

  return (
    <div className='password-reset-container'>
      <form className='password-reset-wrapper'>
        {/* Step 1: Enter Username */}
        {step === 1 && (
          <div className='reset-wrapper'>
            <img src={Key} alt='key illustration' />
            <div className='reset-heading'>
              <span style={{ fontSize: '24px', fontWeight: 600 }}>
                Reset Password
              </span>
              <span style={{ fontSize: '14px' }}>
                Please enter your username used for your account
              </span>
            </div>

            <input
              type='text'
              name='username'
              value={formData.username}
              onChange={handleInputChange}
              required
              placeholder='Username'
              disabled={loading}
            />

            <button type='button' onClick={handleInitiateReset} disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </div>
        )}

        {/* Step 2: Enter Verification Code */}
        {step === 2 && (
          <div>
            <div className='back-arrow-wrapper' onClick={() => setStep(1)}>
              <img src={ArrowLeft} alt='left arrow' />
            </div>

            <div className='reset-wrapper'>
              <img src={Envelope} alt='envelope icon' />
              <div className='reset-heading'>
                <span style={{ fontSize: '24px', fontWeight: 600 }}>
                  Check Your Inbox
                </span>
                <span style={{ fontSize: '14px' }}>
                  A verification code was sent to your email, please enter it below.
                </span>
              </div>

              <input
                type='text'
                name='confirmationCode'
                placeholder='6 digit security code'
                value={formData.confirmationCode}
                onChange={handleInputChange}
                required
              />

              <button type='button' onClick={handleVerifyCode}>
                Verify Code
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Set New Password */}
        {step === 3 && (
          <div>
            <div className='back-arrow-wrapper' onClick={() => setStep(2)}>
              <img src={ArrowLeft} alt='left arrow' />
            </div>
            <div className='reset-wrapper'>
              <img src={Key} alt='key illustration' />
              <span style={{ fontSize: '24px', fontWeight: 600 }}>
                Set New Password
              </span>
<PasswordRequirements password={formData.newPassword} />
              <input
                type='password'
                name='newPassword'
                placeholder='New Password'
                value={formData.newPassword}
                onChange={handleInputChange}
                required
                disabled={loading}
              />

              

              <input
                type='password'
                name='confirmPassword'
                placeholder='Confirm Password'
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                disabled={loading}
              />

              <button type='button' onClick={handleConfirmReset} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className='reset-wrapper'>
            <span style={{ fontSize: '24px', fontWeight: 600 }}>Success!</span>
            <span style={{ fontSize: '14px' }}>
              Your password has been successfully reset.
            </span>
            <button type='button' onClick={handleGoToSignIn}>
              Go to Sign In
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PasswordResetForm;
